import { graphql } from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";
import { UseNodeLogic_LimitedAuthorizationsFragment$key } from "@generated/UseNodeLogic_LimitedAuthorizationsFragment.graphql";
import { UseNodeLogic_NodeFragment$key } from "@generated/UseNodeLogic_NodeFragment.graphql";
import { isPermanentlyBlockedUtil } from "./advancement-result/is-permanently-blocked";
import { calculateDaysDifference } from "../../infecto-lms-webapp/functions/helpers/calculateDaysDifference";
import { useTypedSelector } from "../../infecto-lms-webapp/redux/redux-store";
import { selectAuthSlice } from "../../infecto-lms-webapp/redux/slices/auth.slice";
import { isTemporarilyBlockedUntilUtil } from "../../infecto-lms-webapp/utils/advancement-result/is-temporarily-blocked-until";
import { LMS_WEBAPP_CONFIG_IMPL } from "../../infecto-lms-webapp-impl/config";
import { ProgressBarState } from "../components/nodecard/NodeCardProgressBar.component";

export const LEARN_OPPORTUNITY_FRAGMENT = graphql`
	fragment UseNodeLogic_NodeFragment on LearnOpportunityV2 {
		id
		root {
			id
			shortDescription
		}
		shortDescription
		structureDefinition {
			title
			... on LearnOpportunityRootStructureDefinition {
				extension {
					... on LearnOpportunityRootExtensionImpl {
						internalCode
						dateTimeOnlyAdminsVisibility
						isVisibleOnlyForAdmins
						issuedCertificates {
							issuedAt
							fileRef {
								url
							}
						}
						reducedDataInTree {
							cmePoints
						}
						firstReleasedAt
						firstLearnOpportunityIdWithMultipleChoiceElement
						firstContentNodeIdWithoutMultipleChoiceElement
						heftContentSubmissionInfo {
							hasActiveContentSubmission
						}
						multipleChoiceContentSubmissionInfo {
							hasActiveContentSubmission
						}
						configConsequences {
							rewardExpired
							isRewardedTo
						}
					}
				}
			}
		}
		typeDefinition {
			... on LearnOpportunityELearningContentTypeDefinition {
				containedElementTypes
				contentNodeAdvancementResult {
					status
					... on CanNotBeRestartedAfterFailedContentNodeAdvancementResult {
						status
						configResults {
							configType

							... on RestartIfFailedContentConfigResult {
								canBeRestarted
							}

							... on NegativeBlockPermanentlyWhenRewardedRestartIfFailedContentConfigResult {
								canBeRestarted
							}

							... on NegativeBlockTemporarilyWhenRewardedRestartIfFailedContentConfigResult {
								blockedUntil
							}
						}
					}
				}
			}
			... on LearnOpportunityBranchTypeDefinition {
				children {
					id
					typeDefinition {
						... on LearnOpportunityELearningContentTypeDefinition {
							containedElementTypes
							contentNodeAdvancementResult {
								status
								... on CanNotBeRestartedAfterFailedContentNodeAdvancementResult {
									status
									configResults {
										configType

										... on RestartIfFailedContentConfigResult {
											canBeRestarted
										}

										... on NegativeBlockPermanentlyWhenRewardedRestartIfFailedContentConfigResult {
											canBeRestarted
										}

										... on NegativeBlockTemporarilyWhenRewardedRestartIfFailedContentConfigResult {
											blockedUntil
										}
									}
								}
							}
						}
					}
				}
			}
		}
		...NodeIcon_NodeFragment
		...NodeCardBadgeComponent_Fragment
		...NodeCardDateComponent_Fragment
		...NodeCardDurationAndAwardComponent_Fragment
		...NodeCardFooterContainer_LearnOpportunityFragment
		...NodeCardCertificateButton_Fragment
		...WissenwirktAttachmentHeftDisplay_LearnOpportunityV2Fragment
		...ContentNodesButtons_NodeFragment
	}
`;

const WITHOUT_LEARNSTATE_FRAGMENT = graphql`
	fragment UseNodeLogic_LimitedAuthorizationsFragment on LearnOpportunityV2 {
		id
		structureDefinition {
			title
			... on LearnOpportunityRootStructureDefinition {
				extension {
					... on LearnOpportunityRootExtensionImpl {
						internalCode
						configConsequences {
							rewardExpired
							isRewardedTo
						}
						firstLearnOpportunityIdWithMultipleChoiceElement
						firstReleasedAt
						reducedDataInTree {
							cmePoints
						}
					}
				}
			}
		}
		...NodeIcon_NodeFragment
		...NodeCardBadgeComponent_Fragment
		...NodeCardDurationAndAwardComponent_Fragment
	}
`;
interface OwnProps {
	learnOpportunityFragmentRef?: UseNodeLogic_NodeFragment$key;

	nodeWithoutUserLearnStateFragmentRef?: UseNodeLogic_LimitedAuthorizationsFragment$key;
	ignoreUserLearnState: boolean;
}

export function useNodeLogic({
	learnOpportunityFragmentRef,
	nodeWithoutUserLearnStateFragmentRef,
	ignoreUserLearnState,
}: OwnProps) {
	const nodeWithoutUserLearnStateFragment =
		useFragment<UseNodeLogic_LimitedAuthorizationsFragment$key>(
			WITHOUT_LEARNSTATE_FRAGMENT,
			nodeWithoutUserLearnStateFragmentRef!,
		);
	const learnOpportunityLoggedIn = useFragment<UseNodeLogic_NodeFragment$key>(
		LEARN_OPPORTUNITY_FRAGMENT,
		learnOpportunityFragmentRef!,
	);

	const learnOpportunity = learnOpportunityLoggedIn || nodeWithoutUserLearnStateFragment;

	const authState = useTypedSelector(selectAuthSlice);
	const isLoggedIn = authState.isLoggedIn;

	const extension = learnOpportunity.structureDefinition.extension;

	const multipleChoiceModul = learnOpportunity?.typeDefinition?.children
		? learnOpportunity.typeDefinition.children.filter((c) =>
				c?.typeDefinition?.containedElementTypes?.some((el) => el.includes("multipleChoice")),
			)
		: learnOpportunity?.typeDefinition?.containedElementTypes?.some((el) =>
					el.includes("multipleChoice"),
			  )
			? [learnOpportunity]
			: [];

	const isTemporarilyBlockedUntil =
		multipleChoiceModul.length > 0 &&
		multipleChoiceModul[0]?.typeDefinition?.contentNodeAdvancementResult?.configResults &&
		isTemporarilyBlockedUntilUtil(
			multipleChoiceModul[0].typeDefinition.contentNodeAdvancementResult.configResults,
		);
	const isPermanentlyBlocked =
		multipleChoiceModul.length > 0 &&
		multipleChoiceModul[0]?.typeDefinition?.contentNodeAdvancementResult?.configResults &&
		isPermanentlyBlockedUtil(
			multipleChoiceModul[0].typeDefinition.contentNodeAdvancementResult.configResults,
		);
	const hasCanNotBeRestarteAfterPassedConfig =
		(multipleChoiceModul &&
			multipleChoiceModul[0]?.typeDefinition?.contentNodeAdvancementResult?.status ===
				"CanNotBeRestartedAfterPassed") ||
		false;
	const blockedUntil = !!(
		isTemporarilyBlockedUntil &&
		isTemporarilyBlockedUntil.blockedUntil &&
		isTemporarilyBlockedUntil.blockedUntil.length > 0
	);
	const doesNodeExpiresBeforeUserGetsUnblocked = !!(
		extension?.configConsequences?.isRewardedTo &&
		blockedUntil &&
		calculateDaysDifference(
			extension?.configConsequences?.isRewardedTo,
			isTemporarilyBlockedUntil?.blockedUntil,
		) < 0
	);
	const numIssuedCertificates =
		(!ignoreUserLearnState &&
			extension?.issuedCertificates! && //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
			extension.issuedCertificates?.length) ||
		0;
	const isModulRewarded =
		((extension?.reducedDataInTree?.cmePoints || -1) > 0 &&
			!extension?.configConsequences?.rewardExpired) ||
		numIssuedCertificates > 0;
	const isModulNotRewardedOrIsBlockedOrExpired =
		!extension?.firstLearnOpportunityIdWithMultipleChoiceElement ||
		isPermanentlyBlocked ||
		doesNodeExpiresBeforeUserGetsUnblocked ||
		!isModulRewarded;

	const componentColor = isModulNotRewardedOrIsBlockedOrExpired
		? "#787878"
		: LMS_WEBAPP_CONFIG_IMPL.brandColor;

	const progressBarState: ProgressBarState = isModulNotRewardedOrIsBlockedOrExpired
		? "noRewarded"
		: "isRewarded";

	const hasNoLekButActiveContentSubmission =
		!extension?.firstLearnOpportunityIdWithMultipleChoiceElement &&
		extension?.heftContentSubmissionInfo?.hasActiveContentSubmission === true;

	const isShowingBadge =
		(isModulRewarded && !isPermanentlyBlocked && !doesNodeExpiresBeforeUserGetsUnblocked) ||
		(hasNoLekButActiveContentSubmission &&
			!isPermanentlyBlocked &&
			!doesNodeExpiresBeforeUserGetsUnblocked) ||
		hasCanNotBeRestarteAfterPassedConfig;
	const targetContentNodeIdOpt = extension?.multipleChoiceContentSubmissionInfo
		?.hasActiveContentSubmission
		? extension.firstLearnOpportunityIdWithMultipleChoiceElement
		: learnOpportunity?.typeDefinition?.children?.[0].id ||
			extension?.firstContentNodeIdWithoutMultipleChoiceElement;
	const targetContentNodeId = targetContentNodeIdOpt ? targetContentNodeIdOpt : learnOpportunity.id;

	return {
		learnOpportunity,
		authState,
		isLoggedIn,
		multipleChoiceModul,
		isShowingBadge,
		hasNoLekButActiveContentSubmission,
		progressBarState,
		componentColor,
		isModulNotRewardedOrIsBlockedOrExpired,
		isModulRewarded,
		numIssuedCertificates,
		doesNodeExpiresBeforeUserGetsUnblocked,
		blockedUntil,
		hasCanNotBeRestarteAfterPassedConfig,
		isPermanentlyBlocked,
		isTemporarilyBlockedUntil,
		targetContentNodeId,
	};
}
